import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { useActiveEntries } from "../lib/queries";
import { useResumeActiveEntries } from "../lib/uploadQueue";
import type { Me } from "../lib/types";
import { CaptureButton } from "./CaptureButton";
import { Nav } from "./Nav";
import { UploadTray } from "./UploadTray";
import { UserMenu } from "./UserMenu";

export type AppShellProps = {
  me: Me;
  children: ReactNode;
};

/**
 * The bound book: masthead, page, and a thumb bar at the bottom on phones.
 *
 * It also adopts anything the server says is still in flight, so refreshing the
 * page mid-classification doesn't lose the developing cards.
 */
export function AppShell({ me, children }: AppShellProps) {
  const active = useActiveEntries();
  useResumeActiveEntries(active.data);

  return (
    <div className="grain relative flex min-h-dvh flex-col">
      <a
        href="#page"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:border focus:border-ink focus:bg-card focus:px-3 focus:py-2 focus:font-label focus:text-sm"
      >
        Skip to the page
      </a>

      <header className="sticky top-0 z-30 border-b border-rule bg-paper/92 backdrop-blur-[2px]">
        <div className="mx-auto flex max-w-5xl items-center gap-4 px-safe pt-safe pb-2">
          <Link to="/scrapbook" className="group shrink-0">
            <span className="block display-4 text-2xl leading-none text-ink sm:text-[1.75rem]">
              Foundling
            </span>
            <span className="mt-0.5 hidden font-label text-[0.5625rem] tracking-[0.28em] text-ink-soft uppercase sm:block">
              A scrapbook of things found
            </span>
          </Link>

          <div className="ms-auto flex items-center gap-4">
            <Nav variant="masthead" />
            <CaptureButton variant="bar" className="hidden sm:inline-flex" />
            <UserMenu me={me} />
          </div>
        </div>
      </header>

      <main
        id="page"
        className="mx-auto w-full max-w-5xl flex-1 px-safe pt-6 pb-[calc(6.5rem+env(safe-area-inset-bottom))] sm:pb-16"
      >
        {children}
      </main>

      <footer className="mx-auto hidden w-full max-w-5xl px-safe pb-8 sm:block">
        <div className="hairline mb-3" />
        <p className="font-label text-[0.625rem] tracking-[0.22em] text-ink-soft uppercase">
          Foundling — determinations by Claude, and not to be relied upon for anything you intend to eat
        </p>
      </footer>

      <UploadTray />
      <Nav variant="bar" />
    </div>
  );
}
